<template>
  <div ref="container" class="pbas-data-table-container">
    <v-dialog scrollable v-model="cancelEditDialog" persistent max-width="400">
      <material-card
        :title="$t('components.claim_submission.close_dialog_title')"
        :color="$vuetify.theme.themes.light.error"
        icon="cancel"
      >
        <v-card-text>
          {{ $t("globals.are_you_sure") }}
        </v-card-text>
        <v-card-actions>
          <v-btn color text @click="cancelEditDialog = false">
            {{ $t("components.claim_submission.keep_editing") }}
          </v-btn>
          <v-btn color="primary" text @click="closeClaim">
            {{ $t("globals.close") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable persistent v-model="editDialog" max-width="1800">
      <material-card
        v-if="editDialog"
        :title="
          $t(
            'components.claims.submitted_submissions_table.edit_claim_submission_title_{0}',
            [submissionId]
          )
        "
        :color="$vuetify.theme.themes.light.primary"
        icon="edit"
      >
        <v-card-text>
          <claim-submission-tabs
            ref="cst"
            :claimSubmission="item"
            :memberIdentity="memberIdentity"
            :patients="patients"
            @reload-member-identity="loadMemberDetails"
            :planName="translate2(planIdentity, 'Name')"
            :planNumber="planIdentity.UniqueIdentifier"
            :submitted="true"
            @close="closeClaim"
            :planStartDate="planIdentity.FirstRenewalDate"
            @reload-claim-submission="loadClaimSubmission(item.Id)"
            :hasHSA="planIdentity.HasHSA"
            :planIdentityId="planIdentity.Id"
            :payments="itemPayments"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            color
            text
            @click="
              cancelEditDialog = true;
              refresh();
            "
          >
            {{ $t("globals.close") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable persistent v-model="reviewDialog" max-width="1800">
      <material-card
        v-if="reviewDialog && item"
        :title="
          $t(
            'components.claims.submitted_submissions_table.review_claim_submission_title_{0}',
            [submissionId]
          )
        "
        :color="$vuetify.theme.themes.light.primary"
        icon="edit"
      >
        <v-card-text>
          <claim-submission-tabs
            ref="cst"
            :claimSubmission="item"
            :memberIdentity="memberIdentity"
            :patients="patients"
            @reload-member-identity="loadMemberDetails"
            :planName="translate2(planIdentity, 'Name')"
            :planNumber="planIdentity.UniqueIdentifier"
            :submitted="true"
            :review="true"
            @reload-claim-submission="loadClaimSubmission(item.Id)"
            :planStartDate="planIdentity.FirstRenewalDate"
            @close="closeClaim"
            :hasHSA="planIdentity.HasHSA"
            :planIdentityId="planIdentity.Id"
            :payments="itemPayments"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            color
            text
            @click="
              cancelEditDialog = true;
              refresh();
            "
          >
            {{ $t("globals.close") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable v-model="loadingSubmission" persistent max-width="200">
      <v-card>
        <v-card-title>{{ $t("globals.please_wait") }}</v-card-title>
        <v-card-text>
          <v-container fluid grid-list-md>
            <v-layout row justify-center>
              <v-flex xs12 sm6 md4 lg3>
                <v-progress-circular indeterminate></v-progress-circular>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-data-table
      mobile-breakpoint="0"
      :fixed-header="false"
      class="claims-table"
      :server-items-length="totalCount"
      :options.sync="options"
      :loading="loading"
      :headers="headers"
      :items="claims"
      :items-per-page="50"
      @click:row="(i) => expanded.push(i)"
      show-expand
      show-select
      v-model="selected"
      :expanded.sync="expanded"
      item-key="Identifier"
      :footer-props="{ 'items-per-page-options': [5, 10, 20, 50, 100, 500] }"
      :item-class="itemRowBackground"
    >
      <template v-slot:top>
        <div style="overflow-x: auto" ref="horizontalScroll">
          <div
            style="width: 1px; height: 1px"
            ref="horizontalScrollContent"
          ></div>
        </div>
      </template>
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template v-slot:header.data-table-select="{ props }">
        <v-icon @click="selectAll" color="white" v-if="selected.length === 0">
          mdi-checkbox-blank-outline
        </v-icon>

        <v-simple-checkbox
          @click="unselectAll"
          color="white"
          v-else-if="
            selected.length !== claims.filter((o) => !o.IsPredetermined).length
          "
          v-ripple
          v-bind="props"
          :value="props.indeterminate"
        ></v-simple-checkbox>
        <v-icon v-else @click="unselectAll" color="white">
          mdi-checkbox-outline
        </v-icon>
      </template>
      <template #item.data-table-select="{ item }">
        <td>
          <v-simple-checkbox
            v-if="!item.IsPredetermined"
            :value="item.isSelected"
            @input="select(item)"
          />
        </td>
      </template>
      <template v-slot:[`item.Timestamp`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`timestamp-` + index">
          {{ $helpers.formatDate(item.Timestamp) }}
          <!-- {{ item.Timestamp.split("T")[0] }} -->
        </td>
      </template>
      <template v-slot:[`item.Patients`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`patients-` + index">
          {{ getPatientNames(item) }}
        </td>
      </template>
      <template v-slot:[`item.Coverage`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`coverages-` + index">
          {{ getCoverageNames(item) }}
        </td>
      </template>
      <template v-slot:[`item.ClaimedAmount`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`claimed-` + index">
          <span
            v-if="
              item.Claims.filter(
                (o) =>
                  o.CurrentClaimData.PlanBenefit &&
                  o.CurrentClaimData.PlanBenefit.Benefit.IsSickDay
              ).length > 0
            "
          >
            {{ calculateClaimedAmount(item) }}
          </span>
          <span v-else>
            {{ $helpers.toCurrency(calculateClaimedAmount(item)) }}
          </span>
        </td>
      </template>
      <template v-slot:[`item.UnpaidTotal`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`unpaid-` + index">
          <span
            v-if="
              item.Claims.filter(
                (o) =>
                  o.CurrentClaimData.PlanBenefit &&
                  o.CurrentClaimData.PlanBenefit.Benefit.IsSickDay
              ).length > 0
            "
          >
            -
          </span>
          <span v-else>
            {{ $helpers.toCurrency(calculateUnpaidTotal(item)) }}
          </span>
        </td>
      </template>
      <template v-slot:[`item.PaidTotal`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`paid-` + index">
          {{ $helpers.toCurrency(calculatePaidTotal(item)) }}
        </td>
      </template>
      <template v-slot:[`item.PaymentDate`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`payment-date-` + index">
          {{ $helpers.formatDate(item.PaymentDate) }}
        </td>
      </template>
      <template v-slot:[`item.Payee`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`payee-` + index">
          <span v-if="!item.IsPredetermined">{{ item.Payee }}</span>
        </td>
      </template>
      <!-- <template v-slot:[`item.PaymentMethod`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`coverages-` + index">
          {{ getProofTypes(item) }}
        </td>
      </template> -->
      <template v-slot:[`item.Flags`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`flags-` + index">
          <v-chip
            v-for="flag in item.Flags"
            small
            :color="flag.Color"
            :text-color="flag.FontColor"
            class="text-white"
            :key="flag.Id"
            style="display: inline-flex; margin-top: 5px"
          >
            {{ translate2(flag, "Name") }}
          </v-chip>
          <!-- <span
            v-for="flag in item.Flags"
            :key="flag.Id"
            style="
              background-color: #008080;
              padding: 3px 6px;
              color: white;
              border-radius: 3px;
            "
          >
            {{ translate2(flag, "Name") }}
          </span> -->
        </td>
      </template>
      <template v-slot:[`item.Actions`]="{ item, index }">
        <td class="text-subtitle-2 text-left" :key="`actions-` + index">
          <v-icon
            color="primary"
            class="proof-eye"
            @click="editClaimSubmission(item)"
            v-if="showEditAction(item)"
          >
            edit
          </v-icon>
          <v-icon
            color="primary"
            class="proof-eye"
            @click="reviewClaimSubmission(item)"
            v-if="showReviewAction(item)"
          >
            mdi-file-eye-outline
          </v-icon>
        </td>
      </template>
      <template v-slot:[`header.Print`]="{}">
        <td class="text-subtitle-2">
          <v-btn :disabled="selected.length < 1" icon @click="printSelected">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="white" v-bind="attrs" v-on="on">
                  mdi-printer-outline
                </v-icon>
              </template>
              <span>
                {{ $t(`components.claims_history.table.print_selected`) }}
              </span>
            </v-tooltip>
          </v-btn>
        </td>
      </template>
      <template v-slot:[`item.Print`]="{ item }">
        <td class="text-subtitle-2">
          <v-btn
            v-if="item.IsPredetermined"
            icon
            @click="printPredetermination(item)"
          >
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="accent" v-bind="attrs" v-on="on">
                  mdi-printer-outline
                </v-icon>
              </template>
              <span>
                {{ $t(`globals.print`) }}
              </span>
            </v-tooltip>
          </v-btn>
          <v-btn
            v-else-if="
              item.Claims.some(
                (o) => o.State === 'Rejected' || o.State === 'Paid'
              )
            "
            icon
            @click="printEob(item)"
          >
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="accent" v-bind="attrs" v-on="on">
                  mdi-printer-outline
                </v-icon>
              </template>
              <span>
                {{ $t(`globals.print`) }}
              </span>
            </v-tooltip>
          </v-btn>
        </td>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td
          class="px-0"
          :colspan="headers.length"
          style="width: 100%; background-color: white; padding: 0 !important"
        >
          <claim-detail
            :claim="item"
            @preview-proof="previewProof($event, item)"
          />
        </td>
      </template>
    </v-data-table>
    <div
      ref="headersContainer"
      :style="{
        position: 'fixed',
        top: '0',
        background: 'white',
        width: '100%',
        visibility: 'hidden',
        'overflow-x': 'auto',
        'z-index': 10,
      }"
    >
      <table
        ref="headers"
        class="v-data-table"
        style="border-spacing: 0"
      ></table>
    </div>
    <error-handler :error="error" />
  </div>
</template>

<script>
import ClaimDetail from "@/components/Claims/ClaimDetail.vue";
import { translate2 } from "@/trans2";
import { mapActions, mapGetters } from "vuex";
import ClaimSubmissionTabs from "./ClaimSubmissionTabs.vue";
import ErrorHandler from "@/components/ErrorHandler.vue";

export default {
  components: {
    ClaimDetail,
    ClaimSubmissionTabs,
    ErrorHandler,
  },
  props: {
    loading: Boolean,
    reset: Boolean,
    tableOptions: {
      type: Object,
      default: () => {},
    },
    claims: Array,
    totalCount: Number,
  },
  data() {
    return {
      headersZIndex: 1,
      options: { sortDesc: [true] },
      page: 1,
      expanded: [],
      selected: [],
      item: null,
      editDialog: false,
      memberIdentity: null,
      planIdentity: null,
      cancelEditDialog: false,
      patients: [],
      reviewDialog: false,
      submissionId: null,
      loadingSubmission: false,
      itemPayments: [],
      error: null,
      addedEvents: false,
      timeoutResult: null,
      notEmit: false
    };
  },
  watch: {
    options: {
      handler(v) {
        if (this.notEmit) {
          return;
        }
        this.$emit("pagination", v);
      },
      deep: true,
    },
    selected: {
      handler() {
        this.$emit("claims-selected", {
          headers: this.headers,
          items: this.selected,
          title: this.$t(
            "components.claims_history.printout.submitted_claims_history"
          ),
        });
      },
      deep: true,
      immediate: true,
    },
    reset: {
      handler(v) {
        if (v) this.expanded = [];
      },
    },
  },
  computed: {
    ...mapGetters("plansMembers", {
      columnsDropDownOptions: "options",
    }),
    ...mapGetters("auth", ["user"]),
    headers() {
      return [
        {
          text: "",
          value: "data-table-select",
          class: `text-subtitle-2 ${
            this.$vuetify.breakpoint.smAndUp ? "primary" : ""
          } white--text select-all-checkbox`,
          cellClass: "single-select",
        },
        {
          text: "",
          value: "data-table-expand",
          cellClass: "expand",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("components.claims_history.header.submission_id"),
          align: "center",
          sortable: true,
          value: "Identifier",
          cellClass: "text-subtitle-2 pl-0",
          class: "text-subtitle-2 primary white--text",
          width: "150px",
        },
        {
      text: this.$t("components.claims_history.member_certificate"),
      align: "left",
      sortable: true,
      value: "MemberCertificate",
      cellClass: "text-subtitle-2",
      class: "text-subtitle-2 primary white--text",

    },
        {
          text: this.$t("components.claims_history.header.patient"),
          align: "left",
          sortable: false,
          value: "Patients",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
          width: "150px",
        },
        {
          text: this.$t("globals.coverage"),
          align: "left",
          sortable: false,
          value: "Coverage",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("components.claims_history.header.submission_date"),
          align: "left",
          sortable: true,
          value: "Timestamp",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
          width: "140px",
        },
        {
          text: this.$t("components.claims_history.header.claimed_total"),
          align: "left",
          sortable: false,
          value: "ClaimedAmount",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("components.claims_history.header.total_paid"),
          align: "left",
          sortable: false,
          value: "PaidTotal",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("components.claims_history.header.unpaid_total"),
          align: "left",
          sortable: false,
          value: "UnpaidTotal",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("components.chequerequisitionform.payee"),
          align: "left",
          sortable: false,
          value: "Payee",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("headers.claims_payments.PaymentMethod"),
          align: "left",
          sortable: false,
          value: "PaymentMethod",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("views.planbalance.cheque_number"),
          align: "left",
          sortable: true,
          value: "PaymentNumber",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("headers.claims_payments.PaymentBatchNumber"),
          align: "left",
          sortable: true,
          value: "PaymentBatchNumber",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("headers.claims_payments.PaymentDate"),
          align: "left",
          sortable: true,
          value: "PaymentDate",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("pbascomponents.pbasdatatable.actions"),
          align: "left",
          sortable: false,
          value: "Actions",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: this.$t("views.plangeneralparameters.flags"),
          align: "left",
          sortable: false,
          value: "Flags",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
        },
        {
          text: "",
          sortable: false,
          value: "Print",
          cellClass: "text-subtitle-2",
          class: "text-subtitle-2 primary white--text",
          width: "20px",
        },
      ];
    },
  },
  mounted() {
    window.addEventListener("resize", this.onWindowSizeChanged);
    window.addEventListener("scroll", this.onScroll);
  },
  updated() {
    if (
      this.$el !== undefined &&
      this.$el !== null &&
      this.$el.querySelector !== undefined
    ) {
      this.addScrollEvents();
      this.onScroll();
      this.resizeScrollbar();
    }
  },
  methods: {
    ...mapActions("plansMembers", ["getPlan", "getMemberInfo"]),
    ...mapActions("claims", [
      "getClaimSubmissionById",
      "getPlanBenefitById",
      "getProviderById",
      "getDrugByDIN",
      "getCoveredBenefitById",
      "getPaymentsByClaimSubmissionId",
    ]),
    resetTableOptions() {
      this.notEmit = true;
      this.options.page = 1;
      this.$nextTick(()=>{
        this.notEmit = false;
      })
    },
    resizeScrollbar() {
      if (this.$el.querySelector !== undefined) {
        const wrapper = this.$el.querySelector(".v-data-table__wrapper");
        if (wrapper !== null && wrapper.children.length > 0) {
          const el = wrapper.children[0];
          if (el !== null && this.$refs.horizontalScrollContent !== undefined) {
            this.$refs.horizontalScrollContent.style.width =
              el.offsetWidth + "px";
          }
        }
      }
    },
    addScrollEvents() {
      if (this.addedEvents || this.$el.querySelector === undefined) {
        return;
      }
      const wrapper = this.$el.querySelector(".v-data-table__wrapper");
      if (wrapper !== null) {
        wrapper.addEventListener("scroll", (event) => {
          this.$refs.horizontalScroll.scrollLeft = wrapper.scrollLeft;
          this.$refs.headersContainer.scrollLeft = wrapper.scrollLeft;
        });
        this.$refs.headersContainer.addEventListener("scroll", (event) => {
          wrapper.scrollLeft = this.$refs.headersContainer.scrollLeft;
        });
        this.$refs.horizontalScroll.addEventListener("scroll", (event) => {
          wrapper.scrollLeft = this.$refs.horizontalScroll.scrollLeft;
        });
        this.addedEvents = true;
        setTimeout(this.setupFixedHeader, 100);
      }
    },
    onWindowSizeChanged() {
      this.resizeScrollbar();
      this.onScroll();
    },
    beforeDestroy() {
      window.removeEventListener("resize", this.onWindowSizeChanged);
      window.removeEventListener("scroll", this.onScroll);
    },
    setupFixedHeader() {
      if (
        this.$refs.container !== undefined &&
        this.$el.querySelector !== undefined
      ) {
        const header = this.$el.querySelector(".v-data-table-header");
        if (header !== undefined) {
          this.$refs.headersContainer.style.width =
            this.$refs.container.querySelector(".v-data-table__wrapper")
              .offsetWidth + "px";
          const cln = header.cloneNode(true);
          this.$refs.headers.innerHTML = "";
          this.$refs.headers.append(cln);

          const thead2 = this.$refs.headers.querySelector(
            ".v-data-table-header"
          ).children[0];
          this.$refs.headers.style.width = header.scrollWidth + "px";
          if (this.showSelect) {
            const index = this.showExpand ? 1 : 0;
            thead2.children[index].innerHTML = "";
          }
          for (const c of Object.keys(header.children[0].children)) {
            const source = header.children[0].children[c];
            if (source === undefined) {
              continue;
            }

            if (thead2.children[c].style !== undefined) {
              thead2.children[c].style.width =
                header.children[0].children[c].offsetWidth + "px";
            }
          }
        }
      }
    },
    onScroll() {
      clearTimeout(this.timeoutResult);
      this.timeoutResult = setTimeout(() => {
        this.setupFixedHeader();
        if (
          this.$refs.container === undefined ||
          this.$el.querySelector === undefined
        ) {
          return;
        }
        const header = this.$el.querySelector(".v-data-table-header");
        if (header === undefined) {
          return;
        }
        const tablevisible = this.checkVisible(
          this.$refs.container.querySelector(".v-data-table__wrapper")
        );
        if (!tablevisible) {
          this.$refs.headersContainer.style.visibility = "hidden";
          return;
        }
        const isVisible = this.checkVisible(header);
        this.$refs.headersContainer.style.visibility = isVisible
          ? "hidden"
          : "visible";
      }, 100);
    },
    checkVisible(elm) {
      const rect = elm.getBoundingClientRect();
      const viewHeight = Math.max(
        document.documentElement.clientHeight,
        window.innerHeight
      );
      return !(rect.bottom < 0 || rect.top - viewHeight >= 0);
    },
    refresh() {
      this.$emit("reload");
    },
    isChanged() {
      // console.log("sst: ", this.$refs.cst)
      // return (this.$refs.cst && this.$refs.cst.isChanged()) || (!this.$refs.cst && false);
      return this.editDialog || this.reviewDialog;
    },
    previewProof(proof, item) {
      // console.log(item, proof, this.claims);
      const index = this.claims.findIndex((o) => o.Id === item.Id);
      proof.UploadName = `${index + 1}-${proof.ProofType}-${item.Identifier}`;
      this.$emit("preview-proof", proof);
    },
    translate2(item, field) {
      return translate2(item, field);
    },
    getPatientNames(claim) {
      const patients = claim.Claims.map((c) => {
        return c.FirstName + " " + c.LastName;
      });
      return [...new Set(patients)].join(", ");
    },

    getCoverageNames(claim) {
      const coverages = claim.Claims.map((c) => {
        return `${c.CurrentClaimData.PlanBenefit.Benefit.Code} - ${translate2(
          c.CurrentClaimData.PlanBenefit.Benefit,
          "Name"
        )}`;
      });
      return [...new Set(coverages)].join(", ");
    },

    getProofTypes(claim) {
      // const proofs = [];
      // for (let c of claim.Claims) {
      //   for (let proof of c.CurrentClaimData.Proofs) {
      //     proofs.push()
      //   }
      // }
      const proofs = claim.Claims.map((o) => o.Proofs.map((c) => c.ProofType));
      return [...new Set(proofs.flat(1))].join(", ");
    },

    calculateClaimedAmount(claim) {
      let claimsTotal = 0;
      let claimsCob = 0;
      let totalFee = 0;
      for (let c of claim.Claims) {
        if (c.State === "Deleted") {
          break;
        }
        if (
          c.CurrentClaimData.PlanBenefit &&
          c.CurrentClaimData.PlanBenefit.Benefit.IsSickDay
        ) {
          claimsTotal++;
        } else {
          claimsCob = claimsCob + c.CurrentClaimData.COBAmount;
          claimsTotal = claimsTotal + c.CurrentClaimData.ExpenseAmount;
          let labFee = parseFloat(c.CurrentClaimData.LabFee);
          if (isNaN(labFee)) {
            labFee = 0.0;
          }
          totalFee = totalFee + labFee;
        }
      }
      return claimsTotal + totalFee - claimsCob;
    },
    calculateUnpaidTotal(claim) {
      let total = 0;
      for (let c of claim.Claims.filter(
        (o) =>
          o.State === "Approved" ||
          o.State === "PendingPayment" ||
          o.State === "Paid" ||
          o.State === "Archived"
      )) {
        // if (c.State === "Deleted" || c.State === "Rejected") {
        //   break;
        // }
        if (
          c.CurrentClaimData.PlanBenefit &&
          c.CurrentClaimData.PlanBenefit.Benefit.IsSickDay
        ) {
          break;
        }
        let cob = parseFloat(c.CurrentClaimData.COBAmount);
        if (isNaN(cob)) {
          cob = 0.0;
        }
        let paid = parseFloat(c.CurrentClaimData.PaidAmount);
        if (isNaN(paid)) {
          paid = 0.0;
        }
        let paidLabFee = parseFloat(c.CurrentClaimData.PaidLabFee);
        if (isNaN(paidLabFee)) {
          paidLabFee = 0.0;
        }
        let labFee = parseFloat(c.CurrentClaimData.LabFee);
        if (isNaN(labFee)) {
          labFee = 0.0;
        }
        total =
          total +
          c.CurrentClaimData.ExpenseAmount +
          labFee -
          cob -
          paid -
          paidLabFee;
      }
      return total;
    },
    calculatePaidTotal(claim) {
      let total = 0;
      for (let c of claim.Claims.filter(
        (o) =>
          o.State === "Approved" ||
          o.State === "PendingPayment" ||
          o.State === "Paid" ||
          o.State === "Archived"
      )) {
        // if (c.State === "Deleted" || c.State === "Rejected") {
        //   break;
        // }
        let paid = parseFloat(c.CurrentClaimData.PaidAmount);
        if (isNaN(paid)) {
          paid = 0.0;
        }
        let paidLabFee = parseFloat(c.CurrentClaimData.PaidLabFee);
        if (isNaN(paidLabFee)) {
          paidLabFee = 0.0;
        }
        total = total + paid + paidLabFee;
      }
      return total;
    },
    printSelected() {
      this.$emit("print-selected", {
        headers: this.headers,
        items: this.selected,
        title: this.$t(
          "components.claims_history.printout.submitted_claims_history"
        ),
      });
    },
    printPredetermination(item) {
      // console.log(item);
      this.$emit("print-predetermination", {
        headers: this.headers,
        item: item,
        title: this.$t(
          "components.claims_history.printout.submitted_claims_history"
        ),
      });
    },
    printEob(item) {
      // console.log(item);
      this.$emit("print-eob", {
        headers: this.headers,
        item: item,
        title: this.$t(
          "components.claims_history.printout.submitted_claims_history"
        ),
      });
    },
    async editClaimSubmission(item) {
      try {
        console.log("edit cs #1")
        this.loadingSubmission = true;
        // this.item = JSON.parse(JSON.stringify(item));
        this.submissionId = item.Identifier;

        await this.loadPlanDetails(item.PlanId);
        await this.loadMemberDetails(item.MemberId, item.PlanId);
        await this.loadClaimSubmission(item.Id);

        this.editDialog = true;
        console.log("edit cs #2")
      } catch (error) {
        console.log("edit cs #3", error)
        this.error = error;
      } finally {
        console.log("edit cs #4")
        this.loadingSubmission = false;
      }
    },
    async reviewClaimSubmission(item) {
      try {
        console.log("review cs #1")
        this.loadingSubmission = true;
        // this.item = JSON.parse(JSON.stringify(item));
        this.submissionId = item.Identifier;
        this.reviewDialog = true;
        await this.loadPlanDetails(item.PlanId);
        await this.loadMemberDetails(item.MemberId, item.PlanId);
        await this.loadClaimSubmission(item.Id);
        console.log("review cs #2")
      } catch (error) {
        console.log("review cs #3", error)
        this.error = error;
      } finally {
        console.log("review cs #4")
        this.loadingSubmission = false;
      }
    },
    async loadClaimSubmission(claimSubmissionId) {
      console.log("load claim submission", claimSubmissionId);
      this.loadingSubmission = true;
      try {
        let item = (
          await this.getClaimSubmissionById({
            id: claimSubmissionId,
          })
        ).data;
        for (let claim of item.Claims) {
          claim.Identifier = Math.random().toString();
          if (claim.DependantId) {
            claim.Patient = this.patients.filter(
              (o) => o.IdentityId === claim.DependantId
            )[0];
          } else {
            claim.Patient = this.patients.filter(
              (o) => o.Id === claim.MemberId
            )[0];
          }
          claim.PlanBenefit = (
            await this.getPlanBenefitById({
              id: claim.PlanBenefitId,
            })
          ).data.value[0];
console.log("#1")
          if (
            claim.HSACoveredBenefitId !== null &&
            claim.HSACoveredBenefitId !== undefined &&
            claim.HSACoveredBenefitId !== "00000000-0000-0000-0000-000000000000"
          ) {
            claim.CoveredBenefit = (
              await this.getCoveredBenefitById({
                planIdentityId: claim.PlanId,
                id: claim.HSACoveredBenefitId,
              })
            ).data.value.filter((o) => o.Id === claim.HSACoveredBenefitId)[0];
          }

          console.log(JSON.stringify(claim));
          // let num = parseFloat(claim.ExpenseAmount);
          // console.log(num);
          // if (!isNaN(num)) {
          //   console.log(num.toFixed(2))
          //   claim.ExpenseAmount = num.toFixed(2);
          // } else {
          //   claim.ExpenseAmount = "0.00";
          // }
          // num = parseFloat(claim.COBAmount);
          // if (!isNaN(num)) {
          //   claim.COBAmount = num.toFixed(2);
          // } else {
          //   claim.ExpenseAmount = "0.00";
          // }
          // num = parseFloat(claim.LabFee);
          // if (!isNaN(num)) {
          //   claim.LabFee = num.toFixed(2);
          // } else {
          //   claim.ExpenseAmount = "0.00";
          // }
          if (claim.ToothCodeId !== null && claim.ToothCodeId !== undefined) {
            // console.log("claim has tooth code id: ", claim.ToothCodeId);
            claim.ToothCode =
              this.columnsDropDownOptions.Models.ToothCode.filter(
                (o) => o.Id === claim.ToothCodeId
              )[0];
            // console.log(claim.ToothCode);
            // console.log(this.columnsDropDownOptions.Models.ToothCode);
          }
          console.log("#2")
          if (claim.DIN !== null && claim.DIN !== undefined) {
            const din = (
              await this.getDrugByDIN({
                planIdentityId: claim.PlanId,
                din: claim.DIN,
              })
            ).data.value;
            if (din.length > 0) {
              claim.DIN = din[0];
            } else {
              // claim.DIN = "DIN not found in the database"
              this.error = {
                customMessage: this.$t("components.claims.din_not_found_{0}", [
                  claim.DIN,
                ]),
              };
            }
          }
        }
        console.log("#3")
        for (let proof of item.Proofs) {
          proof.Identifier = Math.random().toString();
        }
        console.log("#4")
        for (let note of item.Notes) {
          note.Identifier = Math.random().toString();
        }
        console.log("#5")
        item.Notes.unshift({
          Identifier: Math.random().toString(),
          Writer: this.user,
        });
        if (item.ProviderId) {
          item.Provider = (
            await this.getProviderById({
              id: item.ProviderId,
            })
          ).data.value[0];
        }
        console.log("#6")
        if (item.Provider !== null && item.Provider !== undefined) {
          if (item.OfficeId) {
            item.Office = item.Provider.ProviderOffices.filter(
              (o) => o.OfficeId === item.OfficeId
            )[0];
          }
        }
        console.log("#7")
        item.Patient = item.Claims[0].Patient;
        item.Id = claimSubmissionId;
        this.item = item;
        this.itemPayments = (
          await this.getPaymentsByClaimSubmissionId({
            id: claimSubmissionId,
          })
        ).data;
        console.log(JSON.stringify(item));
        console.log("#9")
      } catch (error) {
        console.log(error);
        console.log("#10")
        this.error = error;
      } finally {
        console.log("##############################11")
        this.loadingSubmission = false;
        console.log(this.loadingSubmission);
      }
    },
    async loadMemberDetails(memberIdentityId, planIdentityId) {
      if (
        memberIdentityId !== null &&
        memberIdentityId !== undefined &&
        memberIdentityId !== "00000000-0000-0000-0000-000000000000"
      ) {
        // this.$emit("plan-name", this.getPlanName(this.searchForm.planIdentity));
        // this.$emit("plan-number", this.getPlanNumber(this.searchForm.planIdentity));
        // this.loading = true;
        try {
          //CurrentMemberBenefits($expand=CoverageIdentity($expand=Current($expand=Translations($select=LanguageCode,Name)))),
          this.memberIdentity = (
            await this.getMemberInfo({
              planIdentityId: planIdentityId,
              memberIdentityId: memberIdentityId,
              odataQuery:
                "$select=Id,Email,PlanIdentityId,PlanEntryDate&$expand=CurrentMembershipStatus($expand=MemberGroupIdentity($select=Id)),MemberIdentification($select=Certificate),CurrentBankAccount,CurrentMember($select=LastName,FirstName,MiddleName,DateOfBirth),CurrentDependants($select=Id,IdentityId,FirstName,MiddleName,LastName,DateOfBirth;$expand=Relationship($expand=Translations),Identity($select=Code,IsEligible)),CurrentContactInformation($expand=Address($expand=Country($expand=Translations),Province($expand=Translations)))",
            })
          ).data;

          this.getPatients();
          // this.$emit("select", this.memberIdentity);
        } catch (error) {
          this.error = error;
        } finally {
          // this.loading = false;
        }
      } else {
        this.memberIdentity === undefined;
      }
    },
    async loadPlanDetails(planIdentityId) {
      // this.$emit("plan-name", this.getPlanName(this.searchForm.planIdentity));
      // this.$emit("plan-number", this.getPlanNumber(this.searchForm.planIdentity));
      // this.loading = true;
      try {
        //CurrentMemberBenefits($expand=CoverageIdentity($expand=Current($expand=Translations($select=LanguageCode,Name)))),
        this.planIdentity = (await this.getPlan(planIdentityId)).data;

        // this.$emit("select", this.memberIdentity);
      } catch (error) {
        this.error = error;
      } finally {
        // this.loading = false;
      }
    },
    closeClaim() {
      // this.coverageType = null;
      this.cancelEditDialog = false;
      this.editDialog = false;
      this.reviewDialog = false;
      this.item = null;
      this.$emit("reload");
    },
    getPatients() {
      this.patients = this.memberIdentity.CurrentDependants.sort((a, b) => {
        if (Date.parse(a.DateOfBirth) < Date.parse(b.DateOfBirth)) {
          return -1;
        } else if (Date.parse(a.DateOfBirth) === Date.parse(b.DateOfBirth)) {
          return 0;
        } else {
          return 1;
        }
      });
      this.patients.unshift({
        Id: this.memberIdentity.Id,
        FirstName: this.memberIdentity.CurrentMember.FirstName,
        LastName: this.memberIdentity.CurrentMember.LastName,
        MiddleName: this.memberIdentity.CurrentMember.MiddleName,
        DateOfBirth: this.memberIdentity.CurrentMember.DateOfBirth,
      });
    },
    showEditAction(item) {
      // Available only if all claim lines status is New or null
      return (
        item.Claims.filter(
          (o) => o.State === undefined || o.State === null || o.State === "New"
        ).length === item.Claims.length
      );
    },
    showReviewAction(item) {
      // Available only if NOT ALL claim lines status is New or null
      return (
        item.Claims.filter(
          (o) => o.State === undefined || o.State === null || o.State === "New"
        ).length !== item.Claims.length
      );
    },
    itemRowBackground(item) {
      // console.log(item.IsPredetermined);
      return item.IsPredetermined ? "predetermined" : "";
    },
    select(item) {
      if (item.isSelected) {
        this.selected = this.selected.filter(
          (o) => o.Identifier !== item.Identifier
        );
      } else {
        this.selected.push(item);
      }
      item.isSelected = !item.isSelected;
      this.$forceUpdate();
    },
    selectAll() {
      for (let claim of this.claims) {
        if (!claim.IsPredetermined) {
          claim.isSelected = true;
        }
      }
      this.selected = this.claims.filter((o) => !o.IsPredetermined);
      this.$forceUpdate();
      // console.log(
      //   this.selected.length,
      //   this.claims.filter((o) => !o.IsPredetermined).length
      // );
    },
    unselectAll() {
      for (let claim of this.claims) {
        claim.isSelected = false;
      }
      this.selected = [];
      this.$forceUpdate();
    },
  },
};
</script>
<style scoped>
.pbas-data-table-container {
  padding: 0 !important;
}
.pbas-data-table-container >>> .container {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
.pbas-data-table-container >>> .v-data-table table {
  height: 1px;
}
.pbas-data-table-container >>> .v-data-table td {
  padding: 0;
  white-space: pre-line;
  user-select: text;
  height: 100%;
  cursor: text;
}
.pbas-data-table-container >>> .v-data-table__mobile-row {
  height: auto !important;
}
.pbas-data-table-container >>> .v-data-table tr {
  height: 1px;
}

.pbas-data-table-container >>> .v-data-table td > div {
  padding: 5px 16px;
  height: 100%;
  display: flex;
  min-height: 1px;
}
.pbas-data-table-container >>> .v-data-table td > div.v-data-table__checkbox {
  padding-left: 0px;
  margin-left: 16px;
}

.pbas-data-table-container >>> .v-data-table td > div > * {
  align-self: center;
}

.pbas-data-table-container >>> .v-data-table th[role="columnheader"] {
  padding: 5px 16px;
  white-space: nowrap;
  height: auto;
  text-transform: uppercase;
  font-weight: 800;
}

.pbas-data-table-container >>> .v-messages {
  min-height: auto !important;
}

.pbas-data-table-container >>> .v-data-table tbody .v-input__slot {
  margin: 0 !important;
}

.pbas-data-table-container >>> .v-data-table .v-data-table__wrapper {
  max-height: var(--max-height);
}

.pbas-data-table-container
  >>> .v-data-table
  tbody
  td
  .v-input--selection-controls {
  padding: 0 !important;
  margin: 0 !important;
  margin-top: 0 !important;
  margin-top: 4px !important;
  display: inline-block;
}

.pbas-data-table-container >>> .v-data-table__wrapper {
  max-height: 400px;
}

.pbas-data-table-container
  >>> .theme--light.v-data-table
  .v-data-table-header
  th.sortable:hover {
  opacity: 1;
}
.pbas-data-table-container >>> .v-data-table__mobile-row__wrapper {
  display: inline-block;
}

.pbas-data-table-container >>> .v-data-table__mobile-row__wrapper {
  white-space: pre-wrap;
}
.pbas-data-table-container
  >>> .v-data-table
  tbody
  tr.expanded.expanded__content {
  box-shadow: none;
}
.pbas-data-table-container >>> .v-data-table {
  width: 100%;
}
.pbas-data-table-container >>> .v-data-table tbody table {
  background: #fffbe7;
}
.claims-table >>> tbody tr:hover {
  cursor: pointer;
}
.claims-table >>> .v-data-table-header__icon {
  color: white !important;
}
.claims-table >>> th {
  padding: 0 8px !important;
}
.claims-table >>> td {
  padding: 0 8px !important;
}
.claims-table >>> th.select-all-checkbox {
  padding-left: 8px !important;
}
.claims-table >>> td.single-select {
  padding-right: 4px !important;
}
.claims-table >>> td.expand {
  padding-right: 14px !important;
}
.claims-table >>> .v-data-table__expanded__row:not(predetermined) {
  background-color: #cbcbcb !important;
}

.claims-table >>> .v-data-table__expanded__row:is(.predetermined) {
  background-color: rgb(34, 163, 206) !important;
}
.select-all-checkbox >>> .theme--light.v-icon {
  color: white !important;
}
.claims-table >>> .expand {
  padding: 0;
}
.v-tooltip__content {
  max-width: 100px !important;
}
</style>
